import React from 'react';
import { Heart, Briefcase, Users, DollarSign, Home as HomeIcon, BookOpen, Sparkles, Plane } from 'lucide-react';

const LifeCompass = ({ scores = {} }) => {
  const domains = [
    { id: 'health', label: 'Health', icon: Heart, color: '#10b981', angle: 0 },
    { id: 'career', label: 'Career', icon: Briefcase, color: '#f59e0b', angle: 45 },
    { id: 'relationships', label: 'Love', icon: Users, color: '#ec4899', angle: 90 },
    { id: 'wealth', label: 'Wealth', icon: DollarSign, color: '#8b5cf6', angle: 135 },
    { id: 'family', label: 'Family', icon: HomeIcon, color: '#06b6d4', angle: 180 },
    { id: 'education', label: 'Skills', icon: BookOpen, color: '#6366f1', angle: 225 },
    { id: 'spirituality', label: 'Spirit', icon: Sparkles, color: '#d946ef', angle: 270 },
    { id: 'travel', label: 'Travel', icon: Plane, color: '#14b8a6', angle: 315 },
  ];

  const defaultScores = {
    health: 83,
    career: 65,
    relationships: 72,
    wealth: 58,
    family: 78,
    education: 70,
    spirituality: 55,
    travel: 62,
  };

  const finalScores = { ...defaultScores, ...scores };
  const centerX = 150;
  const centerY = 150;
  const maxRadius = 100;

  // Generate radar chart points
  const generatePoints = () => {
    return domains.map((domain, index) => {
      const score = finalScores[domain.id] || 50;
      const radius = (score / 100) * maxRadius;
      const angleRad = (domain.angle * Math.PI) / 180;
      const x = centerX + radius * Math.cos(angleRad);
      const y = centerY + radius * Math.sin(angleRad);
      return { x, y, domain, score };
    }).concat([domains[0]].map((domain) => {
      const score = finalScores[domain.id] || 50;
      const radius = (score / 100) * maxRadius;
      const angleRad = (domain.angle * Math.PI) / 180;
      const x = centerX + radius * Math.cos(angleRad);
      const y = centerY + radius * Math.sin(angleRad);
      return { x, y, domain, score };
    }));
  };

  const points = generatePoints();
  const pathData = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x},${p.y}`).join(' ');

  return (
    <div className="relative w-full flex justify-center">
      <svg width="300" height="300" viewBox="0 0 300 300" className="overflow-visible">
        {/* Background circles */}
        {[0.25, 0.5, 0.75, 1].map((scale) => (
          <circle
            key={scale}
            cx={centerX}
            cy={centerY}
            r={maxRadius * scale}
            fill="none"
            stroke="currentColor"
            strokeWidth="0.5"
            className="text-stone-200"
            opacity={0.3}
          />
        ))}

        {/* Axis lines */}
        {domains.map((domain) => {
          const angleRad = (domain.angle * Math.PI) / 180;
          const x = centerX + maxRadius * Math.cos(angleRad);
          const y = centerY + maxRadius * Math.sin(angleRad);
          return (
            <line
              key={domain.id}
              x1={centerX}
              y1={centerY}
              x2={x}
              y2={y}
              stroke="currentColor"
              strokeWidth="0.5"
              className="text-stone-200"
              opacity={0.3}
            />
          );
        })}

        {/* Radar area */}
        <path
          d={pathData}
          fill="url(#radarGradient)"
          stroke="url(#radarStroke)"
          strokeWidth="2"
          opacity={0.6}
        />

        {/* Gradient definitions */}
        <defs>
          <linearGradient id="radarGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#9b7de8" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#edc161" stopOpacity="0.3" />
          </linearGradient>
          <linearGradient id="radarStroke" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#9b7de8" />
            <stop offset="100%" stopColor="#edc161" />
          </linearGradient>
        </defs>

        {/* Data points */}
        {points.slice(0, -1).map((point, index) => (
          <g key={index}>
            <circle
              cx={point.x}
              cy={point.y}
              r="4"
              fill={point.domain.color}
              className="drop-shadow-md"
            />
          </g>
        ))}

        {/* Domain labels */}
        {domains.map((domain) => {
          const angleRad = (domain.angle * Math.PI) / 180;
          const labelRadius = maxRadius + 30;
          const x = centerX + labelRadius * Math.cos(angleRad);
          const y = centerY + labelRadius * Math.sin(angleRad);
          const Icon = domain.icon;

          return (
            <g key={domain.id}>
              <foreignObject
                x={x - 20}
                y={y - 20}
                width="40"
                height="40"
                className="overflow-visible"
              >
                <div className="flex flex-col items-center justify-center">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center shadow-md"
                    style={{ backgroundColor: domain.color }}
                  >
                    <Icon className="w-4 h-4 text-white" />
                  </div>
                  <span
                    className="text-xs font-medium mt-1"
                    style={{ color: domain.color }}
                  >
                    {finalScores[domain.id]}%
                  </span>
                </div>
              </foreignObject>
            </g>
          );
        })}

        {/* Center text */}
        <text
          x={centerX}
          y={centerY - 10}
          textAnchor="middle"
          className="text-xl font-serif font-medium fill-stone-900"
        >
          Life Compass
        </text>
        <text
          x={centerX}
          y={centerY + 10}
          textAnchor="middle"
          className="text-xs fill-stone-600"
        >
          Overall Balance
        </text>
      </svg>
    </div>
  );
};

export default LifeCompass;